"use client";

import { CommandGroup } from "@universe/ui/components/command";
import { useParams, useRouter } from "next/navigation";
import { useMemo, useState } from "react";

import type { ProductType } from "@/constants";
import { useBalances } from "@/hooks/useBalances";
import useBaseCurrencyConverter from "@/hooks/useBaseCurrencyConverter";
import useCurrencyFormatter from "@/hooks/useCurrencyFormatter";
import { useCustomerAccount, useGlobalData } from "@/providers";
import { compareCurrencyRank } from "@/utils/sortProducts";

import { AccountSelectItem } from "./account-select-item";
import { AccountSelectPopover } from "./account-select-popover";

type BalanceAccountsSelectProps = {
  className?: string;
  placeholder?: string;
  currentAccountId?: string;
  onSelect?: (accountId: string) => void;
  showFlag?: boolean;
  flagSize?: string;
  popoverAlign?: "start" | "center" | "end";
  /** When true, only show the name (not the currency code) in the trigger */
  showNameOnly?: boolean;
};

type BalanceAccountItem = {
  value: string;
  label: string;
  name: string;
  searchIndex: string;
  balance: number;
  type: ProductType;
};

/**
 * Account select component for dashboard/accounts list.
 * Shows only accounts that have balances.
 */
export function BalanceAccountsSelect({
  className,
  placeholder,
  currentAccountId: _currentAccountId,
  onSelect,
  showFlag = false,
  flagSize = "28px",
  popoverAlign = "start",
  showNameOnly = false,
}: BalanceAccountsSelectProps) {
  const [open, setOpen] = useState(false);
  const { productId } = useParams<{ productId: string }>();
  const router = useRouter();

  const { balances, isLoading: isBalancesLoading } = useBalances();
  const { products, isLoadingProducts } = useGlobalData();
  const { customerAccountBaseCurrency } = useCustomerAccount();
  const { formatCurrency } = useCurrencyFormatter();
  const { convert } = useBaseCurrencyConverter();

  const isLoading = isBalancesLoading || isLoadingProducts;

  // Use productId from URL as the source of truth, with local state for user selections
  const [localValue, setLocalValue] = useState("");
  // Derive value: prefer productId from URL, fallback to local selection
  const value = productId || localValue;

  // Build list of accounts with balances
  const accountsList = useMemo(() => {
    const items: BalanceAccountItem[] = [];

    Object.entries(balances).forEach(([productCode, balanceInfo]) => {
      // Only show accounts with positive balances
      if (balanceInfo.balance <= 0) return;

      const productInfo = products?.find((p) => p.code === productCode);
      const isCurrency = productInfo?.resourceType === "currencies";

      items.push({
        value: productCode,
        label: formatCurrency(balanceInfo.balance, productCode) ?? productCode,
        name: productInfo?.name || productCode,
        searchIndex: `${productCode}-${productInfo?.name || productCode}`,
        balance: balanceInfo.balance,
        type: isCurrency ? "currencies" : "digital-assets",
      });
    });

    // Sort: base currency first, then by balance (in base currency)
    return items.sort((a, b) => {
      // Base currency always first
      if (a.value === customerAccountBaseCurrency) return -1;
      if (b.value === customerAccountBaseCurrency) return 1;

      // Then sort by balance
      const balanceA = convert(a.balance, a.value);
      const balanceB = convert(b.balance, b.value);

      if (balanceB !== balanceA) {
        return balanceB - balanceA;
      }

      return compareCurrencyRank(
        { code: a.value, type: a.type },
        { code: b.value, type: b.type }
      );
    });
  }, [
    balances,
    products,
    formatCurrency,
    convert,
    customerAccountBaseCurrency,
  ]);

  // Find selected account, including current route account
  let selectedAccount = accountsList.find((account) => account.value === value);

  // If we're on account detail page and didn't find the account, look it up
  if (!selectedAccount && value && productId) {
    const balanceInfo = balances[value];
    if (balanceInfo) {
      const productInfo = products?.find((p) => p.code === value);
      selectedAccount = {
        value: value,
        label: formatCurrency(balanceInfo.balance, value) ?? value,
        name: productInfo?.name || value,
        searchIndex: `${value}-${productInfo?.name || value}`,
        balance: balanceInfo.balance,
        type: productInfo?.resourceType as ProductType,
      };
    }
  }

  const handleSelect = (accountId: string) => {
    // Close popover first to prevent re-render issues during parent state updates
    setOpen(false);
    setLocalValue(accountId);

    // Navigate to account detail page
    if (productId) {
      router.push(`/accounts/${accountId}`);
    }

    onSelect?.(accountId);
  };

  return (
    <AccountSelectPopover
      open={open}
      onOpenChange={setOpen}
      isLoading={isLoading}
      className={className}
      popoverAlign={popoverAlign}
      selectedValue={selectedAccount?.value}
      selectedName={selectedAccount?.name}
      showFlag={showFlag}
      flagSize={flagSize}
      placeholder={placeholder}
      showNameOnly={showNameOnly}
    >
      <CommandGroup>
        {accountsList.map((account) => (
          <AccountSelectItem
            key={account.searchIndex}
            value={account.value}
            label={account.label}
            name={account.name}
            searchIndex={account.searchIndex}
            isSelected={value === account.value}
            onSelect={() => handleSelect(account.value)}
          />
        ))}
      </CommandGroup>
    </AccountSelectPopover>
  );
}
